/**
 * ATS Simulator - Phase 1 of Resume Reality Check
 * 
 * Simulates how Applicant Tracking Systems actually filter resumes.
 * Because if the ATS filters you out, the process ends there.
 * 
 * @author QuickCruit Engineering Team
 */

const log = (...args) => { if (process.env.DEBUG) console.log(...args); };

class ATSSimulator {
    constructor() {
        this.skillSynonyms = this.initializeSkillSynonyms();
        this.commonMistakes = this.initializeCommonMistakes();
        
        log('🤖 ATS Simulator initialized - Ready for analysis');
    }

    /**
     * Simulate ATS filtering process
     * Returns 60% of total score (ATS is the first gate)
     */
    async simulateATSFiltering(resumeText, jobDescription) {
        const startTime = performance.now();
        
        try {
            // Extract requirements from job posting
            const jobRequirements = this.extractJobRequirements(jobDescription);
            log('📋 ATS Requirements extracted:', jobRequirements);
            
            // Parse resume for ATS-readable content
            const resumeData = this.parseResumeForATS(resumeText);
            log('📄 Resume parsed for ATS:', resumeData);
            
            // Run the ATS analysis checks
            const keywordScore = this.checkKeywordMatching(resumeData, jobRequirements);
            const qualificationScore = this.checkQualificationAlignment(resumeData, jobRequirements);
            const formatScore = 100; // Auto-granted since we control formatting
            
            // Calculate weighted ATS survival score
            const atsScore = Math.round(
                (keywordScore * 0.42) +      // 25/60 = 42% of ATS score
                (qualificationScore * 0.33) + // 20/60 = 33% of ATS score  
                (formatScore * 0.25)          // 15/60 = 25% of ATS score
            );
            
            const analysisTime = Math.round(performance.now() - startTime);
            log(`🤖 ATS simulation completed in ${analysisTime}ms - Score: ${atsScore}%`);
            
            return {
                atsScore: Math.min(atsScore, 95), // Cap at 95%
                breakdown: {
                    keywordMatching: keywordScore,
                    qualificationAlignment: qualificationScore,
                    formatCompatibility: formatScore
                },
                details: this.generateATSFeedback(keywordScore, qualificationScore, resumeData, jobRequirements),
                analysisTime
            };
            
        } catch (error) {
            console.error('❌ ATS simulation failed:', error);
            return this.getFallbackATSResults();
        }
    }

    /**
     * Phase 1A: Keyword Matching (25% of total score)
     * ATS systems are keyword-matching engines
     */
    checkKeywordMatching(resumeData, jobRequirements) {
        const { requiredSkills, preferredSkills } = jobRequirements;
        
        if (requiredSkills.length === 0) {
            console.warn('⚠️ No required skills found - ATS analysis limited');
            return 50; // Fallback score
        }
        
        let requiredMatches = 0;
        let preferredMatches = 0;
        const foundSkills = [];
        const missingCritical = [];
        
        // Check required skills (critical - these MUST be present)
        for (const skill of requiredSkills) {
            if (this.findSkillInResume(skill, resumeData)) {
                requiredMatches++;
                foundSkills.push(skill);
            } else {
                missingCritical.push(skill);
            }
        }
        
        // Check preferred skills (bonus points)
        for (const skill of preferredSkills) {
            if (this.findSkillInResume(skill, resumeData)) {
                preferredMatches++;
                foundSkills.push(skill);
            }
        }
        
        // Calculate score based on matches
        const requiredRate = requiredMatches / requiredSkills.length;
        const preferredRate = preferredSkills.length > 0 ? preferredMatches / preferredSkills.length : 0;
        
        // 80% weight on required, 20% on preferred (ATS prioritizes required)
        const keywordScore = Math.round((requiredRate * 80) + (preferredRate * 20));
        
        log(`🔍 Keyword check: ${requiredMatches}/${requiredSkills.length} required, ${preferredMatches}/${preferredSkills.length} preferred`);
        
        return Math.min(keywordScore, 95);
    }

    /**
     * Phase 1B: Qualification Alignment (20% of total score)
     * Years of experience, education, certifications - objective criteria
     */
    checkQualificationAlignment(resumeData, jobRequirements) {
        const experienceScore = this.checkExperienceRequirements(resumeData, jobRequirements);
        const educationScore = this.checkEducationRequirements(resumeData, jobRequirements);
        const certificationScore = this.checkCertificationRequirements(resumeData, jobRequirements);
        
        // Weight experience heavily (most important for ATS)
        const qualificationScore = Math.round(
            (experienceScore * 0.7) +
            (educationScore * 0.2) +
            (certificationScore * 0.1)
        );
        
        log(`📊 Qualification check: Exp=${experienceScore}%, Edu=${educationScore}%, Cert=${certificationScore}%`);
        
        return Math.min(qualificationScore, 95);
    }

    /**
     * Extract job requirements systematically
     */
    extractJobRequirements(jobDescription) {
        const text = jobDescription.toLowerCase();
        
        return {
            requiredSkills: this.extractRequiredSkills(text),
            preferredSkills: this.extractPreferredSkills(text),
            requiredYears: this.extractRequiredExperience(text),
            requiredEducation: this.extractEducationRequirements(text),
            requiredCertifications: this.extractCertificationRequirements(text),
            seniorityLevel: this.extractSeniorityLevel(text)
        };
    }

    /**
     * Parse resume for ATS-readable content
     */
    parseResumeForATS(resumeText) {
        const text = resumeText.toLowerCase();
        
        return {
            skills: this.extractSkillsFromResume(text),
            experience: this.extractExperienceFromResume(text),
            education: this.extractEducationFromResume(text),
            certifications: this.extractCertificationsFromResume(text),
            totalYears: this.calculateTotalExperience(text)
        };
    }

    /**
     * Find skill in resume with synonym matching
     */
    findSkillInResume(skill, resumeData) {
        const skillLower = skill.toLowerCase();
        
        // Check exact match first
        if (resumeData.skills.includes(skillLower)) {
            return true;
        }
        
        // Check synonyms
        const synonyms = this.skillSynonyms.get(skillLower) || [];
        return synonyms.some(synonym => resumeData.skills.includes(synonym));
    }

    /**
     * Extract required skills from job description
     */
    extractRequiredSkills(jobText) {
        const skills = new Set();
        
        // Look for required/must-have sections
        const requiredSection = this.extractSection(jobText, ['required', 'must have', 'essential', 'mandatory']);
        
        // Extract technical skills using aggressive patterns
        const techPatterns = [
            /\b(javascript|js|typescript|python|java|react|angular|vue|node\.js|express)\b/g,
            /\b(aws|azure|gcp|docker|kubernetes|git|sql|mongodb|postgresql)\b/g,
            /\b(html|css|api|rest|graphql|microservices|agile|scrum)\b/g
        ];
        
        for (const pattern of techPatterns) {
            const matches = requiredSection.match(pattern) || [];
            matches.forEach(match => skills.add(match.toLowerCase()));
        }
        
        // Also check full job text if required section is empty
        if (skills.size === 0) {
            for (const pattern of techPatterns) {
                const matches = jobText.match(pattern) || [];
                matches.forEach(match => skills.add(match.toLowerCase()));
            }
        }
        
        return Array.from(skills);
    }

    /**
     * Extract preferred skills from job description
     */
    extractPreferredSkills(jobText) {
        const skills = new Set();
        
        const preferredSection = this.extractSection(jobText, ['preferred', 'nice to have', 'bonus', 'plus']);
        
        const techPatterns = [
            /\b(javascript|js|typescript|python|java|react|angular|vue|node\.js)\b/g,
            /\b(aws|azure|docker|kubernetes|git|sql|mongodb)\b/g
        ];
        
        for (const pattern of techPatterns) {
            const matches = preferredSection.match(pattern) || [];
            matches.forEach(match => skills.add(match.toLowerCase()));
        }
        
        return Array.from(skills);
    }

    /**
     * Extract required years of experience
     */
    extractRequiredExperience(jobText) {
        const expPatterns = [
            /(\d+)\+?\s*years?\s*(of\s*)?(experience|exp)/gi,
            /(\d+)\+?\s*years?\s*(in|with|working)/gi,
            /minimum\s*(\d+)\s*years?/gi
        ];
        
        let maxYears = 0;
        
        for (const pattern of expPatterns) {
            const matches = jobText.matchAll(pattern);
            for (const match of matches) {
                const years = parseInt(match[1]);
                if (years > maxYears) {
                    maxYears = years;
                }
            }
        }
        
        return maxYears;
    }

    /**
     * Check experience requirements
     */
    checkExperienceRequirements(resumeData, jobRequirements) {
        const candidateYears = resumeData.totalYears;
        const requiredYears = jobRequirements.requiredYears;
        
        if (requiredYears === 0) return 100; // No specific requirement
        
        const ratio = candidateYears / requiredYears;
        
        if (ratio >= 1.2) return 95;  // Significantly exceeds
        if (ratio >= 1.0) return 90;  // Meets requirement
        if (ratio >= 0.8) return 70;  // Close enough
        if (ratio >= 0.6) return 50;  // Somewhat below
        if (ratio >= 0.4) return 30;  // Well below
        return 15; // Far below
    }

    /**
     * Initialize skill synonyms for smart matching
     */
    initializeSkillSynonyms() {
        const synonyms = new Map();
        
        // JavaScript ecosystem
        synonyms.set('javascript', ['js', 'ecmascript', 'es6', 'es2015']);
        synonyms.set('react', ['reactjs', 'react.js', 'jsx']);
        synonyms.set('angular', ['angularjs', 'angular.js']);
        synonyms.set('vue', ['vuejs', 'vue.js']);
        synonyms.set('node.js', ['nodejs', 'node', 'express']);
        
        // Databases
        synonyms.set('postgresql', ['postgres', 'pg']);
        synonyms.set('mongodb', ['mongo', 'nosql']);
        synonyms.set('mysql', ['sql']);
        
        // Cloud
        synonyms.set('aws', ['amazon web services', 'ec2', 's3']);
        synonyms.set('azure', ['microsoft azure']);
        synonyms.set('gcp', ['google cloud', 'google cloud platform']);
        
        return synonyms;
    }

    /**
     * Extract skills from resume text
     */
    extractSkillsFromResume(resumeText) {
        const skills = new Set();
        
        const skillPatterns = [
            /\b(javascript|js|typescript|python|java|react|angular|vue|node\.js)\b/g,
            /\b(aws|azure|gcp|docker|kubernetes|git|sql|mongodb|postgresql)\b/g,
            /\b(html|css|api|rest|graphql|express|django|flask)\b/g
        ];
        
        for (const pattern of skillPatterns) {
            const matches = resumeText.match(pattern) || [];
            matches.forEach(match => skills.add(match.toLowerCase()));
        }
        
        return Array.from(skills);
    }

    /**
     * Calculate total years of experience from resume
     */
    calculateTotalExperience(resumeText) {
        const currentYear = new Date().getFullYear();
        let totalYears = 0;
        
        // Look for date ranges
        const datePatterns = [
            /(\d{4})\s*[-–]\s*present/gi,
            /(\d{4})\s*[-–]\s*(\d{4})/g
        ];
        
        for (const pattern of datePatterns) {
            const matches = resumeText.matchAll(pattern);
            for (const match of matches) {
                if (match[0].toLowerCase().includes('present')) {
                    const startYear = parseInt(match[1]);
                    totalYears += currentYear - startYear;
                } else if (match[2]) {
                    const startYear = parseInt(match[1]);
                    const endYear = parseInt(match[2]);
                    totalYears += endYear - startYear;
                }
            }
        }
        
        return Math.min(totalYears, 30); // Cap at 30 years
    }

    /**
     * Extract text section based on keywords
     */
    extractSection(text, keywords) {
        let content = '';
        const lines = text.split('\n');
        let inSection = false;
        
        for (const line of lines) {
            const lowerLine = line.toLowerCase();
            
            if (keywords.some(keyword => lowerLine.includes(keyword))) {
                inSection = true;
                content += line + ' ';
                continue;
            }
            
            if (inSection && (line.trim() === '' || this.isNewSection(line))) {
                break;
            }
            
            if (inSection) {
                content += line + ' ';
            }
        }
        
        return content || text; // Fallback to full text if no section found
    }

    /**
     * Check if line starts new section
     */
    isNewSection(line) {
        const sections = ['responsibilities', 'requirements', 'qualifications', 'benefits', 'about'];
        return sections.some(section => line.toLowerCase().trim().startsWith(section));
    }

    /**
     * Generate ATS feedback
     */
    generateATSFeedback(keywordScore, qualificationScore, resumeData, jobRequirements) {
        const feedback = {
            survival: keywordScore > 70 && qualificationScore > 70,
            keywordIssues: [],
            qualificationIssues: [],
            recommendations: []
        };
        
        // Keyword issues
        if (keywordScore < 70) {
            const missing = jobRequirements.requiredSkills.filter(skill => 
                !this.findSkillInResume(skill, resumeData)
            );
            feedback.keywordIssues = missing.slice(0, 5); // Top 5 missing
        }
        
        // Qualification issues  
        if (qualificationScore < 70) {
            if (resumeData.totalYears < jobRequirements.requiredYears) {
                feedback.qualificationIssues.push(`${resumeData.totalYears} years experience vs ${jobRequirements.requiredYears} required`);
            }
        }
        
        return feedback;
    }

    /**
     * Stub methods for education and certification checking
     */
    checkEducationRequirements(resumeData, jobRequirements) {
        return 80; // Default score - implement later if needed
    }
    
    checkCertificationRequirements(resumeData, jobRequirements) {
        return 80; // Default score - implement later if needed
    }
    
    extractEducationRequirements(jobText) {
        return null; // Implement later if needed
    }
    
    extractCertificationRequirements(jobText) {
        return []; // Implement later if needed
    }
    
    extractSeniorityLevel(jobText) {
        if (jobText.includes('senior')) return 'senior';
        if (jobText.includes('junior')) return 'junior';
        return 'mid';
    }
    
    extractExperienceFromResume(resumeText) {
        return []; // Implement later if needed
    }
    
    extractEducationFromResume(resumeText) {
        return []; // Implement later if needed
    }
    
    extractCertificationsFromResume(resumeText) {
        return []; // Implement later if needed
    }
    
    initializeCommonMistakes() {
        return {}; // Implement later if needed
    }

    /**
     * Fallback results when ATS simulation fails
     */
    getFallbackATSResults() {
        return {
            atsScore: 50,
            breakdown: {
                keywordMatching: 50,
                qualificationAlignment: 50,
                formatCompatibility: 100
            },
            details: {
                survival: false,
                keywordIssues: ['Unable to analyze keywords'],
                qualificationIssues: ['Unable to analyze qualifications'],
                recommendations: ['Try again with clearer job description']
            },
            analysisTime: 0
        };
    }

    // Simple Levenshtein distance (iterative w/ two rows) for small strings
    _levenshtein(a, b) {
        if (a === b) return 0;
        if (!a.length || !b.length) return Math.max(a.length, b.length);
        const prev = new Array(b.length + 1).fill(0).map((_, i) => i);
        for (let i = 0; i < a.length; i++) {
            let curr = [i + 1];
            for (let j = 0; j < b.length; j++) {
                const insert = curr[j] + 1;
                const del = prev[j + 1] + 1;
                const subst = prev[j] + (a[i] === b[j] ? 0 : 1);
                curr.push(Math.min(insert, del, subst));
            }
            for (let j = 0; j < curr.length; j++) prev[j] = curr[j];
        }
        return prev[b.length];
    }

    _fuzzyContains(word, tokens) {
        return tokens.some(t => this._levenshtein(word, t) <= 1);
    }
}

export default ATSSimulator; 